import {
  BOGGLE_GOLD_TILE_KIND,
  type BoggleSpecialTileKind,
} from '~/boggle/config/specialTiles/kinds'
import {
  DEFAULT_BOGGLE_PLAYGROUND_SPECIAL_TILE_SETTINGS,
  type BoggleBlueTileSettings,
  type BoggleGoldTileSettings,
  type BogglePlaygroundSpecialTileSettings,
  type BogglePurpleTileSettings,
} from '~/boggle/engine/specialTiles'
import { RARE_LETTERS } from './boardDistributions/letterWeights'
import { BOGGLE_UNCOMMON_LETTERS } from './challengeModLetterConstants'
import {
  BOGGLE_ALLOW_DIAGONALS,
  BOGGLE_GRID_SIZE,
  BOGGLE_INITIAL_COPIES,
  BOGGLE_INITIAL_RESETS,
  BOGGLE_INITIAL_SHUFFLES,
  BOGGLE_INITIAL_SWAPS,
  BOGGLE_INITIAL_ZAPS,
  BOGGLE_MIN_WORD_LENGTH,
  BOGGLE_SCORE_BONUS_MAX_LENGTH,
  BOGGLE_SCORE_MODE,
  EXTRA_LETTER_BONUS_TIME,
} from './defaults'

export const BOGGLE_PLAYGROUND_GRID_SIZE_OPTIONS = [
  '3x3',
  '4x3',
  '4x4',
  '5x4',
  '5x5',
  '6x6',
] as const
export const BOGGLE_PLAYGROUND_LETTER_ADJACENCY_OPTIONS = [
  'adjacent',
  'free',
  'non-adjacent',
] as const
export const BOGGLE_PLAYGROUND_MIN_WORD_LENGTH_OPTIONS = [3, 4, 5] as const
export const BOGGLE_PLAYGROUND_SKILL_USE_OPTIONS = [1, 2, 3] as const
export const BOGGLE_PLAYGROUND_UNLIMITED_USES = 999_999

export type BoggleGridSize = `${number}x${number}`

export type BoggleGridDimensions = {
  cols: number
  rows: number
}

export type BoggleLetterAdjacencyMode =
  (typeof BOGGLE_PLAYGROUND_LETTER_ADJACENCY_OPTIONS)[number]

export type BogglePlaygroundSkillSettings = {
  enabled: boolean
  uses: null | number
}

export type BogglePlaygroundResetSkillSettings = BogglePlaygroundSkillSettings & {
  delaySeconds: null | number
}

export type BogglePlaygroundGoldTileSettings = BoggleGoldTileSettings
export type BogglePlaygroundBlueTileSettings = BoggleBlueTileSettings
export type BogglePlaygroundPurpleTileSettings = BogglePurpleTileSettings

export type BogglePlaygroundComboSettings = {
  enabled: boolean
  startingValue: number
  wordIncrement: number
}

export type BogglePlaygroundComboSettingsInput =
  Partial<BogglePlaygroundComboSettings> & {
    /** Legacy field; use `boardClearBonusPoints` on the parent settings object. */
    boardClearBonus?: number
  }

export type BoggleScoreMode = 'fixed-one' | 'per-cleared-tile'

export type BogglePlaygroundMeterSegmentSettings = {
  scoreBonus: number
  size: number
}

export type BogglePlaygroundMeterLengthBandSettings = {
  base: number
  letterOffset: number
  maxLength: null | number
  minLength: number
  perLetter: number
}

export type BogglePlaygroundMeterWordChangeSettings = {
  base: number
  letterOffset: number
  lengthBands: readonly BogglePlaygroundMeterLengthBandSettings[]
  perLetter: number
  /** Multiplier applied to the summed meter increase when replaying a word. */
  repeatScalar: number
  uncommonLetter: {
    letters: readonly string[]
    perOccurrence: number
  }
  rareLetter: {
    letters: readonly string[]
    perOccurrence: number
  }
}

export type BogglePlaygroundMeterWordChangeSettingsInput = Partial<
  Omit<
    BogglePlaygroundMeterWordChangeSettings,
    'lengthBands' | 'rareLetter' | 'uncommonLetter'
  >
> & {
  lengthBands?: readonly Partial<BogglePlaygroundMeterLengthBandSettings>[]
  rareLetter?: Partial<BogglePlaygroundMeterWordChangeSettings['rareLetter']>
  uncommonLetter?: Partial<BogglePlaygroundMeterWordChangeSettings['uncommonLetter']>
}

export type BogglePlaygroundMeterBurstTrigger = 'meter-full' | 'timer-end'

export type BogglePlaygroundMeterBurstEffect =
  | 'pause-tile-spawn'
  | 'replace-interval-timer'
  | 'virtual-gold'

export type BogglePlaygroundMeterBurstSettings = {
  drain: {
    drainRampSeconds: number
    durationSeconds: number
  }
  effects: readonly BogglePlaygroundMeterBurstEffect[]
  enabled: boolean
  id: string
  trigger: BogglePlaygroundMeterBurstTrigger
}

export type BogglePlaygroundMeterSettings = {
  capacity: number
  decrease: {
    backspace: number
    clearSelection: number
    invalidWord: number
  }
  enabled: boolean
  tileSpawn: {
    /** Spawn period at 100% displayed charge (`r = 1`). */
    baseIntervalSeconds: number
    enabled: boolean
    /**
     * Power-law exponent for charge-driven spawn cadence:
     * `intervalSeconds = baseIntervalSeconds * r^(-powerExponent)` where
     * `r = (value + overmeter) / capacity` and `r > 0`.
     */
    powerExponent: number
    tileKind: BoggleSpecialTileKind
  }
  bursts: readonly BogglePlaygroundMeterBurstSettings[]
  increase: {
    acceptedWord: BogglePlaygroundMeterWordChangeSettings
  }
  naturalDecayPerSecond: number
  segments: readonly BogglePlaygroundMeterSegmentSettings[]
  startingValue: number
}

export type BogglePlaygroundMeterSettingsInput = Partial<
  Omit<
    BogglePlaygroundMeterSettings,
    'bursts' | 'decrease' | 'increase' | 'segments' | 'tileSpawn'
  >
> & {
  bursts?: readonly Partial<BogglePlaygroundMeterBurstSettings>[]
  decrease?: Partial<BogglePlaygroundMeterSettings['decrease']>
  tileSpawn?: Partial<BogglePlaygroundMeterSettings['tileSpawn']>
  increase?: {
    acceptedWord?: BogglePlaygroundMeterWordChangeSettingsInput
  }
  segments?: readonly Partial<BogglePlaygroundMeterSegmentSettings>[]
}

export type BogglePlaygroundPreset = {
  color?: string
  createdAt: number
  id: string
  isBuiltIn?: boolean
  name: string
  settings: BogglePlaygroundSettings
  updatedAt: number
}

export type BogglePlaygroundSettings = {
  allowDiagonals: boolean
  allowRepeats: boolean
  boardClearBonusPoints: null | number
  combo: BogglePlaygroundComboSettings
  extraLetterBonusTime: number
  endIntervalOnSubmit: boolean
  gridSize: BoggleGridSize
  intervalCount: null | number
  intervalTimeSeconds: number
  letterAdjacencyMode: BoggleLetterAdjacencyMode
  lettersRepopulate: boolean
  /** Delay between each new letter tile spawning in after clears (ms). 0 = all at once. */
  tileSpawnIntervalMs: number
  meter: BogglePlaygroundMeterSettings
  minWordLength: number
  redrawBoardAfterWord: boolean
  rotationEnabled: boolean
  scoreBonusMaxLength: null | number
  scoreMode: BoggleScoreMode
  specialTiles: BogglePlaygroundSpecialTileSettings
  skills: {
    copy: BogglePlaygroundSkillSettings
    reset: BogglePlaygroundResetSkillSettings
    shuffle: BogglePlaygroundSkillSettings
    swap: BogglePlaygroundSkillSettings
    zap: BogglePlaygroundSkillSettings
  }
}

export type BogglePlaygroundSettingsInput = Omit<
  Partial<BogglePlaygroundSettings>,
  'combo' | 'meter' | 'skills' | 'specialTiles'
> & {
  combo?: BogglePlaygroundComboSettingsInput
  meter?: BogglePlaygroundMeterSettingsInput
  skills?: Partial<{
    [Mode in keyof BogglePlaygroundSettings['skills']]: Partial<
      BogglePlaygroundSettings['skills'][Mode]
    >
  }>
  specialTiles?: Partial<BogglePlaygroundSpecialTileSettings>
}

export const DEFAULT_BOGGLE_PLAYGROUND_METER_SEGMENTS = [
  { scoreBonus: 1, size: 1 },
  { scoreBonus: 2, size: 1.3 },
  { scoreBonus: 3, size: 1.7 },
  { scoreBonus: 4, size: 2.2 },
  { scoreBonus: 5, size: 2.9 },
] as const

/** Default meter uncommon set — must match `BOGGLE_UNCOMMON_LETTERS` (board/challenge accents). */
export const DEFAULT_BOGGLE_METER_UNCOMMON_LETTERS = BOGGLE_UNCOMMON_LETTERS

/** Default meter rare set — must match `RARE_LETTERS` (board/challenge accents). */
export const DEFAULT_BOGGLE_METER_RARE_LETTERS = RARE_LETTERS

export const DEFAULT_BOGGLE_PLAYGROUND_SETTINGS: BogglePlaygroundSettings = {
  allowDiagonals: BOGGLE_ALLOW_DIAGONALS,
  allowRepeats: true,
  boardClearBonusPoints: null,
  combo: {
    enabled: false,
    startingValue: 0,
    wordIncrement: 0,
  },
  extraLetterBonusTime: EXTRA_LETTER_BONUS_TIME,
  endIntervalOnSubmit: false,
  gridSize: BOGGLE_GRID_SIZE,
  intervalCount: null,
  intervalTimeSeconds: 30,
  letterAdjacencyMode: 'adjacent',
  lettersRepopulate: false,
  tileSpawnIntervalMs: 500,
  meter: {
    capacity: 20,
    decrease: {
      backspace: 0,
      clearSelection: 0,
      invalidWord: 0,
    },
    enabled: false,
    tileSpawn: {
      baseIntervalSeconds: 3,
      enabled: false,
      powerExponent: 0.74,
      tileKind: BOGGLE_GOLD_TILE_KIND,
    },
    bursts: [],
    increase: {
      acceptedWord: {
        base: 0,
        letterOffset: 0,
        lengthBands: [],
        perLetter: 0,
        repeatScalar: 0,
        uncommonLetter: {
          letters: DEFAULT_BOGGLE_METER_UNCOMMON_LETTERS,
          perOccurrence: 0,
        },
        rareLetter: {
          letters: DEFAULT_BOGGLE_METER_RARE_LETTERS,
          perOccurrence: 0,
        },
      },
    },
    naturalDecayPerSecond: 0,
    segments: DEFAULT_BOGGLE_PLAYGROUND_METER_SEGMENTS,
    startingValue: 0,
  },
  minWordLength: BOGGLE_MIN_WORD_LENGTH,
  redrawBoardAfterWord: false,
  rotationEnabled: false,
  scoreBonusMaxLength: BOGGLE_SCORE_BONUS_MAX_LENGTH,
  scoreMode: BOGGLE_SCORE_MODE,
  specialTiles: DEFAULT_BOGGLE_PLAYGROUND_SPECIAL_TILE_SETTINGS,
  skills: {
    copy: { enabled: false, uses: BOGGLE_INITIAL_COPIES },
    reset: { delaySeconds: null, enabled: false, uses: BOGGLE_INITIAL_RESETS },
    shuffle: { enabled: false, uses: BOGGLE_INITIAL_SHUFFLES },
    swap: { enabled: false, uses: BOGGLE_INITIAL_SWAPS },
    zap: { enabled: false, uses: BOGGLE_INITIAL_ZAPS },
  },
}
